import { Column } from './ColumnsStore'

interface ReconciliationResult {
  reconciledColumns: Column[]
  reconciledKnownIds: string[]
  hasChanges: boolean
}

/**
 * Reconciles stored column preferences with current table columns.
 *
 * This runs once during table initialization to sync localStorage with
 * the current column definitions. It handles:
 * - Removing stale columns (no longer exist in table)
 * - Adding new columns (based on defaultColumnVisibility)
 * - Updating knownColumnIds to include new columns
 *
 * @param storedColumns - User's saved visible columns from localStorage
 * @param storedKnownIds - All column IDs that existed when user last saved
 * @param allCurrentColumns - All columns currently in the table
 * @param defaultColumnVisibility - Default visibility settings for columns
 * @returns Reconciled columns, known IDs, and whether changes were made
 */
export function reconcileStoredColumns(
  storedColumns: Column[] | undefined,
  storedKnownIds: string[] | undefined,
  allCurrentColumns: Column[],
  defaultColumnVisibility: Record<string, boolean> | undefined
): ReconciliationResult {
  // No known columns = first-time user, nothing to reconcile
  if (!storedKnownIds) {
    return {
      reconciledColumns: storedColumns ?? [],
      reconciledKnownIds: [],
      hasChanges: false,
    }
  }

  const knownIds = new Set(storedKnownIds)
  const currentIds = new Set(allCurrentColumns.map((c) => c.id))

  // Find new columns (in current but not in known)
  const newColumnIds = [...currentIds].filter((id) => !knownIds.has(id))

  // Find stale columns (in known but not in current)
  const staleColumnIds = [...knownIds].filter((id) => !currentIds.has(id))

  // No changes needed
  if (newColumnIds.length === 0 && staleColumnIds.length === 0) {
    return {
      reconciledColumns: storedColumns ?? [],
      reconciledKnownIds: storedKnownIds,
      hasChanges: false,
    }
  }

  // Remove stale columns from selectedColumns
  const reconciledColumns = (storedColumns ?? []).filter((c) =>
    currentIds.has(c.id)
  )

  // Update knownColumnIds: remove stale, add new
  const reconciledKnownIds = storedKnownIds.filter((id) => currentIds.has(id))
  reconciledKnownIds.push(...newColumnIds)

  // Add new columns that should be visible by default (appended to end)
  for (const columnId of newColumnIds) {
    const shouldBeVisible = defaultColumnVisibility?.[columnId] === true
    if (shouldBeVisible) {
      const column = allCurrentColumns.find((c) => c.id === columnId)
      if (column && !column.isHidden) {
        reconciledColumns.push(column)
      }
    }
  }

  return { reconciledColumns, reconciledKnownIds, hasChanges: true }
}
